/**
 * <XtsScene> — Top-level r3f scene contents (everything inside <Canvas>).
 *
 * Wiring layer: lights (<SceneLighting>), navigation (OrbitControls +
 * optional ViewCube), the orientation root, and per-XPU groups that
 * mount <ProcessingUnitRoot> together with stations / areas / heatmap /
 * dimensions / info-bar overlays.
 *
 * Module/Mover GLBs are loaded via Suspense; `useGltfClone` handles caching.
 */

import React, {
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';
import {
  OrbitControls,
  OrthographicCamera,
  GizmoHelper,
  GizmoViewcube,
  Grid,
  Stats,
} from '@react-three/drei';
import type { ThreeEvent } from '@react-three/fiber';
import { useThree } from '@react-three/fiber';
import {
  Box3,
  Vector3,
  type OrthographicCamera as ThreeOrthographicCamera,
  type PerspectiveCamera as ThreePerspectiveCamera,
} from 'three';
import type { OrbitControls as OrbitControlsImpl } from 'three-stdlib';
import { useAutoPauseOnHidden } from './useAutoPauseOnHidden.js';
import { useDemandRendering } from './useDemandRendering.js';
import { useShadowAutoUpdate } from './useShadowAutoUpdate.js';
import { ScenePostProcessing } from './ScenePostProcessing.js';
import { MoverPositionStoreContext } from './MoverPositionStore.js';
import {
  buildChain,
  type BuiltChain,
} from '../../geometry/ChainBuilder.js';
import { resolveMoverGlbUrl } from '../../assets/AssetManifest.js';
import { composeAssetUrl } from '../../assets/AssetLoader.js';
import { XtsStations } from './XtsStations.js';
import { XtsAreas } from './XtsAreas.js';
import { XtsStopPositionMovers } from './XtsStopPositionMovers.js';
import { XtsStatorHeatmap } from './XtsStatorHeatmap.js';
import { XtsDimensions } from './XtsDimensions.js';
import { XtsInfoBars } from './XtsInfoBars.js';
import { XtsCustomAssets } from './XtsCustomAssets.js';
import { SceneLighting } from './SceneLighting.js';
import {
  ProcessingUnitRoot,
  type SceneSidecarOverrides,
} from './ProcessingUnitRoot.js';
import { computeBoundingBox, computeTopDownOrtho } from './viewerHelpers.js';
import type {
  AreaConfig,
  AssetManifest,
  CameraProjection,
  CameraState,
  DisplayOptions,
  InfoBarConfig,
  ModuleHighlight,
  ModuleRef,
  MoverRef,
  PartTransformation,
  SelectionState,
  StationConfig,
  StatorHeatmap,
  TrackTransform,
} from '../../types.js';
import type { NormalizedXtsConfig } from '../../geometry/normalizeXtsConfig.js';
import type { HepcoGfxRailProfileDims } from '../../geometry/HepcoGfxRailProfile.js';

export type { SceneSidecarOverrides };

const DEG2RAD = Math.PI / 180;

interface XtsSceneProps {
  config: NormalizedXtsConfig;
  assetsBaseUrl: string;
  assetManifest?: AssetManifest;
  initialCamera?: CameraState;
  overlayFps?: boolean;
  showGrid?: boolean;
  shadows?: boolean;
  shadowOnDemand?: boolean;
  demandRendering?: boolean;
  environmentLighting?: boolean;
  environmentIntensity?: number;
  display?: DisplayOptions;
  selection?: SelectionState;
  selectionColors?: { module?: string; mover?: string };
  moduleHighlights?: ModuleHighlight[];
  onModuleClick?: (ref: ModuleRef, e: ThreeEvent<MouseEvent>) => void;
  onMoverClick?: (ref: MoverRef, e: ThreeEvent<MouseEvent>) => void;
  onEmptyPointerMissed?: () => void;
  performance?: {
    autoPauseOnHidden?: boolean;
    instancing?: boolean;
    instancingThreshold?: number;
    demandRendering?: boolean;
    maxDpr?: number;
    shadowOnDemand?: boolean;
    webgpu?: boolean;
  };
  sidecarOverrides?: SceneSidecarOverrides;
  trackTransformOverrides?: Record<number, TrackTransform>;
  hepcoGfxProfile?: Partial<HepcoGfxRailProfileDims>;
  viewCube?: {
    enabled?: boolean;
    alignment?:
      | 'top-left'
      | 'top-right'
      | 'bottom-left'
      | 'bottom-right'
      | 'top-center'
      | 'bottom-center'
      | 'center-left'
      | 'center-right'
      | 'center-center';
    marginPx?: [number, number];
  };
  onOrbitReady?: (controls: OrbitControlsImpl) => void;
  /** Forwards the active default camera to the parent (perspective or ortho). */
  onCameraReady?: (
    camera: ThreePerspectiveCamera | ThreeOrthographicCamera,
  ) => void;
  /** Live camera projection. Default `'perspective'`. */
  projection?: CameraProjection;
  /** Frame straight down in orthographic mode. Default `true`. */
  topDown?: boolean;
  /** Interaction locks. */
  lock?: { rotate?: boolean; pan?: boolean; zoom?: boolean; selection?: boolean };
  /** Perspective camera start position (mirrors the Canvas `camera` prop). */
  initialCameraPosition?: [number, number, number];
  statorHeatmap?: StatorHeatmap;
}

export const XtsScene: React.FC<XtsSceneProps> = ({
  config,
  assetsBaseUrl,
  assetManifest,
  showGrid = true,
  display,
  selection,
  selectionColors,
  moduleHighlights,
  onModuleClick,
  onMoverClick,
  performance,
  sidecarOverrides,
  trackTransformOverrides,
  hepcoGfxProfile,
  shadows = false,
  shadowOnDemand = false,
  demandRendering = true,
  environmentLighting = true,
  environmentIntensity = 0.4,
  viewCube,
  overlayFps = false,
  onOrbitReady,
  onCameraReady,
  projection = 'perspective',
  topDown = true,
  lock,
  statorHeatmap,
}) => {
  const moverStore = useContext(MoverPositionStoreContext);
  useAutoPauseOnHidden(performance?.autoPauseOnHidden !== false, demandRendering);
  useDemandRendering(demandRendering, moverStore);
  useShadowAutoUpdate(shadowOnDemand);
  const orientation = config.orientation ?? {};
  const orientationPos = orientation.positionMm ?? [0, 0, 0];
  const orientationRot = orientation.rotationDegEuler ?? [0, 0, 0];

  const showStations = display?.displayStations !== false;
  const showAreas = display?.displayAreas !== false;
  const showDimensions = display?.displayDimensions === true;

  // Live 2D plan view: orthographic + straight-down framing.
  const orthoMode = projection === 'orthographic' && topDown !== false;
  // Scene AABB (stable per config) drives the ortho frustum + controls target.
  const sceneBbox = useMemo(
    () => computeBoundingBox(config, trackTransformOverrides),
    [config, trackTransformOverrides],
  );
  const orthoCenter = useMemo<[number, number, number]>(() => {
    const c = sceneBbox.getCenter(new Vector3());
    return [c.x, c.y, c.z];
  }, [sceneBbox]);
  // OrbitControls interaction flags. In ortho top-down, rotation is auto-locked
  // (a 2D plan has no meaningful orbit) unless `lock.rotate` is explicitly false.
  const enableRotate = orthoMode
    ? lock?.rotate === false
    : lock?.rotate !== true;
  const enablePan = lock?.pan !== true;
  const enableZoom = lock?.zoom !== true;

  // Pre-build chains shared by all overlays to avoid duplicate buildChain.
  const chainsAll = useMemo(() => {
    const map = new Map<number, BuiltChain>();
    for (const xpu of config.processingUnits) {
      for (const part of xpu.parts) {
        map.set(part.globalNumber, buildChain(part.modules));
      }
    }
    return map;
  }, [config]);

  const stationsForAllXpus: StationConfig[] = config.stations ?? [];
  const areasForAllXpus: AreaConfig[] = config.areas ?? [];
  const infoBarsByXpuObjectId = useMemo(() => {
    const m = new Map<number, InfoBarConfig[]>();
    for (const b of config.infoBars ?? []) {
      const arr = m.get(b.processingUnitObjectId);
      if (arr) arr.push(b);
      else m.set(b.processingUnitObjectId, [b]);
    }
    return m;
  }, [config.infoBars]);

  return (
    <>
      <SceneLighting
        shadows={shadows}
        environmentLighting={environmentLighting}
        environmentIntensity={environmentIntensity}
      />
      <ScenePostProcessing
        ssao={display?.ssao}
        ssaoIntensity={display?.ssaoIntensity}
        ssaoRadius={display?.ssaoRadius}
        bloom={display?.bloom}
        bloomIntensity={display?.bloomIntensity}
        bloomThreshold={display?.bloomThreshold}
        bloomRadius={display?.bloomRadius}
      />
      {showGrid && (
        <Grid
          args={[5000, 5000]}
          cellSize={100}
          sectionSize={500}
          cellColor="#506070"
          sectionColor="#90A0B0"
          // Three's Grid is XZ; we rotate to XY-plane (Z-up world).
          rotation={[-Math.PI / 2, 0, 0]}
          fadeDistance={5000}
        />
      )}
      {orthoMode && (
        <TopDownOrthoCamera bbox={sceneBbox} paddingFactor={1.1} />
      )}
      <OrbitControls
        makeDefault
        enableDamping
        dampingFactor={0.1}
        enableRotate={enableRotate}
        enablePan={enablePan}
        enableZoom={enableZoom}
        rotateSpeed={1}
        panSpeed={1}
        zoomSpeed={1}
        target={orthoMode ? orthoCenter : [0, 0, 0]}
      />
      {onOrbitReady && <ControlsCapture onReady={onOrbitReady} />}
      {onCameraReady && <CameraCapture onReady={onCameraReady} />}
      {overlayFps && <Stats />}
      {viewCube?.enabled && (
        <GizmoHelper
          alignment={viewCube.alignment ?? 'bottom-right'}
          margin={viewCube.marginPx ?? [80, 80]}
        >
          {/* Z-up label remap. drei's `faces` array is ordered
              [+X, -X, +Y, -Y, +Z, -Z] and its defaults assume Y-up. */}
          <GizmoViewcube
            faces={['Right', 'Left', 'Front', 'Back', 'Top', 'Bottom']}
            color="#1f5fa8"
            opacity={0.92}
            strokeColor="#0e1116"
            textColor="#ffffff"
            hoverColor="#2a7ad6"
          />
        </GizmoHelper>
      )}
      <group
        position={orientationPos}
        rotation={[
          orientationRot[0] * DEG2RAD,
          orientationRot[1] * DEG2RAD,
          orientationRot[2] * DEG2RAD,
          'XYZ',
        ]}
      >
        {config.customAssets && config.customAssets.length > 0 && (
          <XtsCustomAssets assets={config.customAssets} />
        )}
        {config.processingUnits.map((xpu) => {
          const xpuChainsByOid = new Map<number, BuiltChain>();
          const xpuChainsByPartObjectId = new Map<number, BuiltChain>();
          // Per-part transforms keyed by both `globalNumber` (for components
          // that key by partOid) and `objectId` (for InfoBars).
          const xpuPartTransformsByOid = new Map<
            number,
            PartTransformation | undefined
          >();
          const xpuPartTransformsByPartObjectId = new Map<
            number,
            PartTransformation | undefined
          >();
          for (const part of xpu.parts) {
            const chain = chainsAll.get(part.globalNumber);
            if (chain) {
              xpuChainsByOid.set(part.globalNumber, chain);
              xpuChainsByPartObjectId.set(part.objectId, chain);
            }
            xpuPartTransformsByOid.set(
              part.globalNumber,
              part.partTransformation,
            );
            xpuPartTransformsByPartObjectId.set(
              part.objectId,
              part.partTransformation,
            );
          }
          const xpuInfoBars = infoBarsByXpuObjectId.get(xpu.objectId) ?? [];
          // Effective track transform = override (live calibration) over
          // config value (persistent). All XPU-rooted children inherit it.
          const tt =
            trackTransformOverrides?.[xpu.objectId] ??
            xpu.trackTransform ??
            null;
          // Default lift = 200 mm (table-top height). Must match
          // DEFAULT_TRACK_Z_MM in geometry/trackTransform.ts so raycasting
          // / bounds math agrees with the rendered scene.
          const ttPos = tt?.positionMm ?? [0, 0, 200];
          const ttRot = tt?.rotationDegEuler ?? [0, 0, 0];
          const ttScale = tt?.scaleUniform ?? 1;
          const ttVisible = tt?.visible !== false;
          return (
            <group
              key={xpu.objectId}
              name={`xpu-root-${xpu.objectId}`}
              position={ttPos}
              rotation={[
                ttRot[0] * DEG2RAD,
                ttRot[1] * DEG2RAD,
                ttRot[2] * DEG2RAD,
                'XYZ',
              ]}
              scale={ttScale}
              visible={ttVisible}
            >
              <ProcessingUnitRoot
                xpu={xpu}
                assetsBaseUrl={assetsBaseUrl}
                assetManifest={assetManifest}
                display={display}
                selection={selection}
                selectionColors={selectionColors}
                moduleHighlights={moduleHighlights}
                onModuleClick={onModuleClick}
                onMoverClick={onMoverClick}
                sidecarOverrides={sidecarOverrides}
                customAssets={config.customAssets}
                instancing={performance?.instancing === true}
                {...(hepcoGfxProfile ? { hepcoGfxProfile } : {})}
              />
              {showStations && (
                <XtsStations
                  stations={stationsForAllXpus.filter((s) =>
                    s.partOids.some((oid) => xpuChainsByOid.has(oid)),
                  )}
                  chainsByPartOid={xpuChainsByOid}
                  partTransformsByPartOid={xpuPartTransformsByOid}
                  display={display}
                  {...(xpu.positionFrame
                    ? { positionFrame: xpu.positionFrame }
                    : {})}
                />
              )}
              {display?.showStopPositionMovers && (
                <XtsStopPositionMovers
                  stations={stationsForAllXpus.filter((s) =>
                    s.partOids.some((oid) => xpuChainsByOid.has(oid)),
                  )}
                  chainsByPartOid={xpuChainsByOid}
                  partTransformsByPartOid={xpuPartTransformsByOid}
                  moverType={xpu.moverType}
                  glbUrl={composeAssetUrl(
                    assetsBaseUrl,
                    resolveMoverGlbUrl(xpu.moverType, assetManifest?.movers),
                  )}
                  assetsBaseUrl={assetsBaseUrl}
                  {...(display?.stopPositionMoverOptions
                    ? { options: display.stopPositionMoverOptions }
                    : {})}
                  {...(xpu.positionFrame
                    ? { positionFrame: xpu.positionFrame }
                    : {})}
                />
              )}
              {showAreas && areasForAllXpus.length > 0 && (
                <XtsAreas
                  areas={areasForAllXpus.filter((a) =>
                    a.partOids.some((oid) => xpuChainsByOid.has(oid)),
                  )}
                  chainsByPartOid={xpuChainsByOid}
                  partTransformsByPartOid={xpuPartTransformsByOid}
                  display={display}
                  {...(xpu.positionFrame
                    ? { positionFrame: xpu.positionFrame }
                    : {})}
                />
              )}
              {display?.showStatorHeatmap &&
                statorHeatmap &&
                statorHeatmap.parts.length > 0 && (
                  <XtsStatorHeatmap
                    chainsByPartOid={xpuChainsByOid}
                    partTransformsByPartOid={xpuPartTransformsByOid}
                    parts={statorHeatmap.parts.filter((p) =>
                      xpuChainsByOid.has(p.partOid),
                    )}
                    min={statorHeatmap.min}
                    max={statorHeatmap.max}
                    {...(statorHeatmap.minColor
                      ? { minColor: statorHeatmap.minColor }
                      : {})}
                    {...(statorHeatmap.maxColor
                      ? { maxColor: statorHeatmap.maxColor }
                      : {})}
                    {...(statorHeatmap.thicknessMm !== undefined
                      ? { thicknessMm: statorHeatmap.thicknessMm }
                      : {})}
                    {...(statorHeatmap.displacementMm !== undefined
                      ? { displacementMm: statorHeatmap.displacementMm }
                      : {})}
                    {...(statorHeatmap.lateralDisplacementMm !== undefined
                      ? {
                          lateralDisplacementMm:
                            statorHeatmap.lateralDisplacementMm,
                        }
                      : {})}
                    {...(statorHeatmap.segmentsPerModule !== undefined
                      ? {
                          segmentsPerModule: statorHeatmap.segmentsPerModule,
                        }
                      : {})}
                    {...(statorHeatmap.opacity !== undefined
                      ? { opacity: statorHeatmap.opacity }
                      : {})}
                    {...(xpu.positionFrame
                      ? { positionFrame: xpu.positionFrame }
                      : {})}
                  />
                )}
              {showDimensions && (
                <XtsDimensions
                  chainsByPartOid={xpuChainsByOid}
                  partTransformsByPartOid={xpuPartTransformsByOid}
                  railSystem={xpu.railSystem ?? 'Beckhoff'}
                  display={display}
                  {...(xpu.positionFrame
                    ? { positionFrame: xpu.positionFrame }
                    : {})}
                />
              )}
              {xpuInfoBars.length > 0 && (
                <XtsInfoBars
                  infoBars={xpuInfoBars}
                  chainsByPartObjectId={xpuChainsByPartObjectId}
                  partTransformsByPartObjectId={xpuPartTransformsByPartObjectId}
                  {...(xpu.positionFrame
                    ? { positionFrame: xpu.positionFrame }
                    : {})}
                />
              )}
            </group>
          );
        })}
      </group>
    </>
  );
};

/**
 * Forwards the active OrbitControls instance to the parent on mount.
 *
 * `<OrbitControls makeDefault />` registers itself on `useThree().controls`
 * inside its own effect, which fires AFTER the Canvas's `onCreated`
 * callback. Reading `controls` synchronously from `onCreated` would
 * therefore yield `undefined`. This component subscribes to the store
 * slice so the callback fires exactly once when the handle becomes
 * available.
 */
const ControlsCapture: React.FC<{
  onReady: (controls: OrbitControlsImpl) => void;
}> = ({ onReady }) => {
  const controls = useThree(
    (state) => state.controls as OrbitControlsImpl | null,
  );
  useEffect(() => {
    if (controls) onReady(controls);
  }, [controls, onReady]);
  return null;
};

/**
 * Forwards the active default camera to the parent. Fires whenever the default
 * camera changes — e.g. when <TopDownOrthoCamera makeDefault> mounts (switch to
 * 2D) or unmounts (back to the Canvas perspective camera). Keeps the imperative
 * ref handle pointing at the currently rendering camera.
 */
const CameraCapture: React.FC<{
  onReady: (camera: ThreePerspectiveCamera | ThreeOrthographicCamera) => void;
}> = ({ onReady }) => {
  const camera = useThree((state) => state.camera);
  useEffect(() => {
    onReady(camera as ThreePerspectiveCamera | ThreeOrthographicCamera);
  }, [camera, onReady]);
  return null;
};

/**
 * Orthographic top-down ("2D") camera. Rendered with `makeDefault` so it takes
 * over the default camera at runtime WITHOUT remounting the WebGL context. The
 * frustum is fitted to the scene AABB + canvas aspect via the shared
 * `computeTopDownOrtho` helper, so the live 2D view matches
 * `exportScreenshot({ mode: 'top-down' })`. Re-fits on resize (aspect change)
 * and on AABB change (config update).
 */
const TopDownOrthoCamera: React.FC<{
  bbox: Box3;
  paddingFactor: number;
}> = ({ bbox, paddingFactor }) => {
  const camRef = useRef<ThreeOrthographicCamera>(null);
  const size = useThree((s) => s.size);
  const controls = useThree((s) => s.controls as OrbitControlsImpl | null);
  const aspect = size.height > 0 ? size.width / size.height : 1;
  const frustum = useMemo(
    () => computeTopDownOrtho(bbox, aspect, paddingFactor),
    [bbox, aspect, paddingFactor],
  );
  useLayoutEffect(() => {
    const cam = camRef.current;
    if (!cam) return;
    cam.left = frustum.left;
    cam.right = frustum.right;
    cam.top = frustum.top;
    cam.bottom = frustum.bottom;
    cam.near = frustum.near;
    cam.far = frustum.far;
    cam.up.set(...frustum.up);
    cam.position.set(...frustum.position);
    cam.updateProjectionMatrix();
    cam.lookAt(new Vector3(...frustum.target));
    if (controls) {
      controls.target.set(...frustum.target);
      controls.update();
    }
  }, [frustum, controls]);
  return (
    <OrthographicCamera
      ref={camRef}
      makeDefault
      up={frustum.up}
      near={frustum.near}
      far={frustum.far}
      left={frustum.left}
      right={frustum.right}
      top={frustum.top}
      bottom={frustum.bottom}
      position={frustum.position}
    />
  );
};

/** Re-export so consumers building their own scenes can grab raw helpers. */
export type { XtsConfig } from '../../types.js';
